import React, { useState, useRef, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  Fade,
  Tooltip,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import { ReactComponent as GuardiaLogo } from '../../assets/logo_guardia.svg';
import { logAction, formatReportNumber, LOG_ACTIONS } from '../../utils/logClient';
import { ReportContext } from '../Context';

// Fonction pour supprimer les balises HTML et convertir en texte brut
const stripHtmlTags = (html) => {
  if (!html || typeof html !== 'string') return '';

  // Créer un élément DOM temporaire pour parser le HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Récupérer le texte brut
  const textContent = tempDiv.textContent || tempDiv.innerText || '';

  // Nettoyer les espaces multiples et les retours à la ligne
  return textContent
    .replace(/\s+/g, ' ')  // Remplacer les espaces multiples par un seul espace
    .replace(/\n\s*\n/g, '\n')  // Nettoyer les lignes vides multiples
    .trim();
};

// Composant pour afficher le texte corrigé avec les modifications mises en évidence
const CorrectedTextDisplay = ({ originalText, correctedText, differences, isDark }) => {
  // Si aucune différence, afficher le texte normal
  if (!differences || differences.length === 0) {
    return (
      <Typography
        sx={{
          color: isDark ? '#ecf0f1' : 'text.primary',
          whiteSpace: 'pre-wrap',
          fontFamily: 'inherit'
        }}
      >
        {correctedText}
      </Typography>
    );
  }

  // Si le texte est identique, pas de changements
  if (originalText === correctedText) {
    return (
      <Typography
        sx={{
          color: isDark ? '#ecf0f1' : 'text.primary',
          whiteSpace: 'pre-wrap',
          fontFamily: 'inherit'
        }}
      >
        {correctedText}
      </Typography>
    );
  }

  // Afficher le texte avec les parties modifiées mises en évidence
  const parts = [];
  let lastIndex = 0;

  differences.forEach((diff, index) => {
    if (diff.original && diff.position) {
      // Texte avant la différence
      if (diff.position.start > lastIndex) {
        parts.push({
          text: correctedText.slice(lastIndex, diff.position.start),
          type: 'normal'
        });
      }

      // Texte corrigé mis en évidence
      parts.push({
        text: diff.corrected,
        type: 'corrected',
        explanation: diff.explanation,
        original: diff.original
      });

      lastIndex = diff.position.end;
    }
  });

  // Texte après la dernière différence
  if (lastIndex < correctedText.length) {
    parts.push({
      text: correctedText.slice(lastIndex),
      type: 'normal'
    });
  }

  return (
    <Typography
      sx={{
        color: isDark ? '#ecf0f1' : 'text.primary',
        whiteSpace: 'pre-wrap',
        fontFamily: 'inherit',
        lineHeight: 1.6
      }}
    >
      {parts.map((part, index) => {
        if (part.type === 'corrected') {
          return (
            <Tooltip
              key={index}
              title={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Correction appliquée :
                  </Typography>
                  <Typography variant="body2">
                    "{part.original}" → "{part.text}"
                  </Typography>
                  <Typography variant="caption" sx={{ mt: 1, fontStyle: 'italic' }}>
                    {part.explanation}
                  </Typography>
                </Box>
              }
              placement="top"
              arrow
            >
              <span
                style={{
                  backgroundColor: isDark ? 'rgba(255, 215, 0, 0.3)' : 'rgba(255, 215, 0, 0.4)',
                  borderRadius: '3px',
                  padding: '2px 4px',
                  border: `1px solid ${isDark ? 'rgba(255, 215, 0, 0.6)' : 'rgba(255, 215, 0, 0.8)'}`,
                  cursor: 'help',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = isDark ? 'rgba(255, 215, 0, 0.4)' : 'rgba(255, 215, 0, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = isDark ? 'rgba(255, 215, 0, 0.3)' : 'rgba(255, 215, 0, 0.4)';
                }}
              >
                {part.text}
              </span>
            </Tooltip>
          );
        }
        return <span key={index}>{part.text}</span>;
      })}
    </Typography>
  );
};

// Découpe un texte en jetons (mots, ponctuation, espaces) avec indices de positions
const tokenizeWithIndices = (text) => {
  const regex = /([\p{L}\p{N}]+|[^\p{L}\p{N}\s]+|\s+)/gu;
  const tokens = [];
  const wordTokenIndices = [];
  const wordValues = [];

  let match;
  while ((match = regex.exec(text)) !== null) {
    const value = match[0];
    const start = match.index;
    const end = start + value.length;
    const isWord = /[\p{L}\p{N}]/u.test(value[0]);
    tokens.push({ value, start, end, isWord });
    if (isWord) {
      wordTokenIndices.push(tokens.length - 1);
      wordValues.push(value);
    }
  }

  return { tokens, wordTokenIndices, wordValues };
};

// LCS simple (mot-à-mot) pour trouver les paires correspondantes entre original et corrigé
const computeLcsPairs = (aWords, bWords) => {
  const n = aWords.length;
  const m = bWords.length;
  const dp = Array(n + 1)
    .fill(null)
    .map(() => Array(m + 1).fill(0));

  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      if (aWords[i] === bWords[j]) {
        dp[i][j] = dp[i + 1][j + 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
  }

  const pairs = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (aWords[i] === bWords[j]) {
      pairs.push([i, j]);
      i += 1;
      j += 1;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      i += 1;
    } else {
      j += 1;
    }
  }

  return pairs;
};

// Convertit une plage d'indices de mots en plage d'indices de caractères
const wordRangeToCharRange = (tokens, wordTokenIndices, startWordIdx, endWordIdxInclusive) => {
  if (startWordIdx > endWordIdxInclusive) {
    return null;
  }
  const tokenStartIdx = wordTokenIndices[startWordIdx];
  const tokenEndIdx = wordTokenIndices[endWordIdxInclusive];
  return {
    start: tokens[tokenStartIdx].start,
    end: tokens[tokenEndIdx].end
  };
};

// Fonction pour calculer des différences locales entre le texte original et corrigé
const calculateDifferences = (original, corrected) => {
  const differences = [];

  if (!original && !corrected) return differences;
  if (original === corrected) return differences;

  // Tokenisation (mots, ponctuation, espaces) + listes de mots pour LCS
  const origTok = tokenizeWithIndices(original);
  const corrTok = tokenizeWithIndices(corrected);

  const aWords = origTok.wordValues;
  const bWords = corrTok.wordValues;

  // Si très peu de mots ou aucun mot en commun, fallback: tout le texte
  if (aWords.length === 0 || bWords.length === 0) {
    if (corrected && corrected.trim().length > 0) {
      differences.push({
        type: 'correction',
        original: original,
        corrected: corrected,
        position: { start: 0, end: corrected.length },
        explanation: generateCorrectionExplanation(original, corrected)
      });
    }
    return differences;
  }

  const pairs = computeLcsPairs(aWords, bWords); // paires [iOriginalWord, jCorrectedWord]

  let i = 0; // index dans aWords
  let j = 0; // index dans bWords

  const flushChange = (iStart, iEnd, jStart, jEnd) => {
    // Plages inclusives sur les indices de mots; si vide, on ignore
    if (jStart <= jEnd) {
      const corrRange = wordRangeToCharRange(
        corrTok.tokens,
        corrTok.wordTokenIndices,
        jStart,
        jEnd
      );
      const origRange = iStart <= iEnd
        ? wordRangeToCharRange(
            origTok.tokens,
            origTok.wordTokenIndices,
            iStart,
            iEnd
          )
        : null;

      if (corrRange) {
        const correctedSlice = corrected.slice(corrRange.start, corrRange.end);
        const originalSlice = origRange ? original.slice(origRange.start, origRange.end) : '';
        differences.push({
          type: 'correction',
          original: originalSlice,
          corrected: correctedSlice,
          position: { start: corrRange.start, end: corrRange.end },
          explanation: generateCorrectionExplanation(originalSlice, correctedSlice)
        });
      }
    }
  };

  // Parcours des paires communes pour identifier les zones modifiées entre elles
  for (let k = 0; k < pairs.length; k += 1) {
    const [mi, mj] = pairs[k];
    // Si des mots ont changé entre (i..mi-1) et (j..mj-1), c'est une modification
    if (i < mi || j < mj) {
      flushChange(i, mi - 1, j, mj - 1);
    }
    // Avancer au prochain mot commun
    i = mi + 1;
    j = mj + 1;
  }

  // Reste après la dernière correspondance
  if (i < aWords.length || j < bWords.length) {
    flushChange(i, aWords.length - 1, j, bWords.length - 1);
  }

  // Trier par position et fusionner d'éventuels chevauchements
  differences.sort((d1, d2) => d1.position.start - d2.position.start);
  const merged = [];
  for (const d of differences) {
    if (merged.length === 0) {
      merged.push(d);
      continue;
    }
    const last = merged[merged.length - 1];
    if (d.position.start <= last.position.end) {
      // Chevauchement: fusion simple sur la plage, concatener les textes
      last.position.end = Math.max(last.position.end, d.position.end);
      last.corrected = corrected.slice(last.position.start, last.position.end);
      last.original = `${last.original}`; // garder l'original le plus ancien
    } else {
      merged.push(d);
    }
  }

  return merged;
};

// Fonction pour générer une explication de la correction
const generateCorrectionExplanation = (original, corrected) => {
  if (!original && corrected) return "Ajout de texte";
  if (original && !corrected) return "Suppression de texte";
  if (original.length !== corrected.length) return "Modification du texte";

  // Analyse plus fine des différences
  if (original.toLowerCase() === corrected.toLowerCase()) {
    return "Correction de la casse";
  }

  // Vérifier les corrections courantes
  if (original.match(/fait/i) && corrected.match(/constat/i)) {
    return "Remplacement du verbe faible 'faire' par 'constater'";
  }
  if (original.match(/dit/i) && corrected.match(/déclar/i)) {
    return "Remplacement du verbe faible 'dire' par 'déclarer'";
  }
  if (original.match(/voit/i) && corrected.match(/aperç/i)) {
    return "Remplacement du verbe faible 'voir' par 'apercevoir'";
  }
  if (original.match(/nous/i) && corrected.match(/Nous/i)) {
    return "Application du 'Nous majestatif'";
  }

  return "Correction orthographique ou grammaticale";
};

const GuardiaCheck = ({
  rubric,
  editContents,
  guardiaAccess,
  guardiaListenAccess,
  isDark,
  onCorrectionComplete,
  onTranscriptionCompleted
}) => {
  const { report } = useContext(ReportContext);
  const [correctionData, setCorrectionData] = useState({});
  const [correctionLoading, setCorrectionLoading] = useState(false);
  const [showCorrectionBox, setShowCorrectionBox] = useState(false);
  const [error, setError] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [listenError, setListenError] = useState('');
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try {
          mediaRecorderRef.current.stop();
        } catch (error) {
          console.warn("Erreur lors de l'arrêt du MediaRecorder (GuardiaCheck):", error);
        }
      }

      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => {
          try {
            track.stop();
          } catch (error) {
            console.warn("Erreur lors de l'arrêt d'une piste audio (GuardiaCheck):", error);
          }
        });
        audioStreamRef.current = null;
      }
    };
  }, []);

  // Fonction pour corriger le contenu avec GuardIA
  const handleCorrectWithGuardia = async () => {
    if (!guardiaAccess.hasAccess) {
      setError(guardiaAccess.reason);
      return;
    }

    const contentToCorrect = editContents[rubric.id] || rubric.content;
    if (!contentToCorrect || !contentToCorrect.trim()) {
      setError('Le contenu de la rubrique est vide. Veuillez ajouter du contenu avant de le corriger.');
      return;
    }

    // Nettoyer le contenu HTML pour obtenir du texte brut
    const cleanContentToCorrect = stripHtmlTags(contentToCorrect);

    // Mettre à jour l'état de chargement
    setCorrectionLoading(true);
    setShowCorrectionBox(true);
    setError(null);

    try {
      // Appeler l'API GuardIA avec le profil GuardiaCheck
      const token = sessionStorage.getItem('authToken');
      const response = await fetch('/api/guardia/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: cleanContentToCorrect,
          profile: 'GuardiaCheck'
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Analyser les différences entre le texte original et corrigé
        const originalText = cleanContentToCorrect; // Utiliser le texte brut nettoyé
        const correctedText = stripHtmlTags(data.response); // Nettoyer aussi la réponse si elle contient du HTML

        // Calculer les vraies différences
        const differences = calculateDifferences(originalText, correctedText);

        setCorrectionData({
          originalText,
          correctedText,
          differences,
          rubricId: rubric.id,
          timestamp: new Date().toISOString()
        });

        // Log de l'utilisation de GuardIA Check
        logAction(LOG_ACTIONS.GUARDIA_CHECK, formatReportNumber(report?.number, report?.year), {
          rubricId: rubric.id,
          rubricTitle: rubric.title || null,
          correctionsCount: differences.length
        });

        // Appeler la callback si fournie
        if (onCorrectionComplete) {
          onCorrectionComplete(rubric.id, correctedText);
        }
      } else {
        setError(`Erreur lors de la correction : ${data.message || 'Erreur inconnue'}`);
        setShowCorrectionBox(false);
      }
    } catch (error) {
      console.error('Erreur lors de l\'appel à l\'API GuardIA:', error);
      setError('Erreur de connexion à GuardIA. Vérifiez que le serveur est démarré.');
      setShowCorrectionBox(false);
    } finally {
      setCorrectionLoading(false);
    }
  };

  const appendTranscript = (transcript) => {
    if (typeof onTranscriptionCompleted === 'function') {
      onTranscriptionCompleted(transcript);
    }
  };

  const guardiaListen = async (audioBlob) => {
    if (!audioBlob || audioBlob.size === 0) {
      setListenError('Aucun audio détecté. Veuillez réessayer.');
      return;
    }

    setIsTranscribing(true);
    setListenError('');

    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'guardia-check-recording.webm');
      formData.append('language', 'fr');

      const token = sessionStorage.getItem('authToken');
      const response = await fetch('/api/guardia/listen', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      let data = null;
      try {
        const clonedResponse = response.clone();
        data = await response.json();
        if (!response.ok && clonedResponse) {
          const rawText = await clonedResponse.text();
          if (rawText) {
            console.error('Réponse brute guardiaListen (GuardiaCheck):', rawText);
          }
        }
      } catch (jsonError) {
        console.error('Réponse JSON invalide de guardiaListen (GuardiaCheck):', jsonError);
        try {
          const rawText = await response.text();
          if (rawText) {
            console.error('Réponse brute guardiaListen (GuardiaCheck) après échec JSON:', rawText);
          }
        } catch (rawError) {
          console.error('Impossible de lire la réponse brute guardiaListen (GuardiaCheck):', rawError);
        }
      }

      if (!response.ok || !data || !data.success) {
        const errorMessage = data?.message || data?.error || "Impossible de transcrire l'audio.";
        throw new Error(errorMessage);
      }

      const transcript = (data.transcript || '').trim();
      if (transcript) {
        appendTranscript(transcript);
      } else {
        setListenError('Transcription vide. Veuillez réessayer.');
      }
    } catch (error) {
      console.error('Erreur de transcription GuardIA (GuardiaCheck):', error);
      setListenError(error.message || 'Erreur inattendue lors de la transcription.');
    } finally {
      setIsTranscribing(false);
    }
  };

  const stopAudioStream = () => {
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => {
        try {
          track.stop();
        } catch (error) {
          console.warn("Erreur lors de l'arrêt d'une piste audio (GuardiaCheck):", error);
        }
      });
      audioStreamRef.current = null;
    }
  };

  const stopListening = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      setIsListening(false);
      try {
        recorder.stop();
      } catch (error) {
        console.warn("Erreur lors de l'arrêt du MediaRecorder (GuardiaCheck):", error);
      }
    } else {
      setIsListening(false);
      mediaRecorderRef.current = null;
      audioChunksRef.current = [];
      stopAudioStream();
    }
  };

  const startListening = async () => {
    if (isListening || isTranscribing) {
      return;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setListenError("La capture audio n'est pas supportée par ce navigateur.");
      return;
    }

    try {
      setListenError('');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream);
      audioChunksRef.current = [];
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.addEventListener('dataavailable', (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      });

      mediaRecorder.addEventListener('error', (event) => {
        console.error('Erreur MediaRecorder (GuardiaCheck):', event.error);
        setListenError(event.error?.message || "Erreur lors de l'enregistrement audio.");
        setIsListening(false);
        mediaRecorderRef.current = null;
        audioChunksRef.current = [];
        stopAudioStream();
      });

      mediaRecorder.addEventListener('stop', () => {
        setIsListening(false);
        const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType || 'audio/webm' });
        audioChunksRef.current = [];
        mediaRecorderRef.current = null;
        stopAudioStream();

        if (audioBlob.size === 0) {
          setListenError('Aucun audio détecté. Veuillez réessayer.');
          return;
        }

        guardiaListen(audioBlob);
      });

      mediaRecorder.start();
      setIsListening(true);
    } catch (error) {
      console.error("Erreur lors de l'activation du micro GuardIA (GuardiaCheck):", error);
      stopAudioStream();
      mediaRecorderRef.current = null;
      audioChunksRef.current = [];
      setIsListening(false);

      if (error.name === 'NotAllowedError' || error.name === 'SecurityError') {
        setListenError("Accès au micro refusé. Veuillez autoriser l'accès pour utiliser la dictée.");
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        setListenError("Aucun micro n'a été détecté.");
      } else {
        setListenError(error.message || "Impossible de démarrer l'enregistrement audio.");
      }
    }
  };

  const toggleMicrophone = () => {
    if (!guardiaListenAccess?.hasAccess) {
      alert(guardiaListenAccess?.reason || 'La dictée GuardIA Listen est désactivée.');
      return;
    }
    
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  // Fonction pour fermer l'encadré de correction
  const handleCloseCorrectionBox = () => {
    setShowCorrectionBox(false);
    setCorrectionData({});
    setError(null);
  };

  return (
    <Box>
      {/* Bouton Corriger avec GuardIA et bouton micro */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2, mb: 2, gap: 2 }}>
        {/* Bouton Corriger avec GuardIA - masqué */}
        {false && (
          <Button
            variant="contained"
            startIcon={
              <GuardiaLogo
                style={{
                  width: '16px',
                  height: '16px',
                  color: 'white'
                }}
              />
            }
            onClick={handleCorrectWithGuardia}
            disabled={!guardiaAccess.hasAccess || correctionLoading}
            sx={{
              backgroundColor: guardiaAccess.hasAccess ? (isDark ? '#c084fc' : '#800080') : (isDark ? '#555' : '#ccc'),
              color: 'white',
              borderRadius: '8px',
              '&:hover': guardiaAccess.hasAccess ? {
                backgroundColor: '#9932cc',
              } : {},
              '& .MuiButton-startIcon': {
                color: 'white'
              },
              '&.Mui-disabled': {
                backgroundColor: isDark ? '#555' : '#ccc',
                color: 'white'
              }
            }}
          >
            {correctionLoading ? 'Correction en cours...' : 'Corriger avec GuardIA'}
          </Button>
        )}

        {/* Bouton micro et indicateur d'écoute */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={toggleMicrophone}
            disabled={!guardiaListenAccess?.hasAccess || (isTranscribing && !isListening)}
            sx={{
              p: 1,
              borderRadius: '50%',
              backgroundColor: !guardiaListenAccess?.hasAccess
                ? (isDark ? '#555' : '#ccc')
                : isListening
                ? (isDark ? '#ff5722' : '#f44336')
                : (isDark ? '#2d3f4c' : 'grey.100'),
              color: !guardiaListenAccess?.hasAccess
                ? (isDark ? '#7f8c8d' : 'grey.400')
                : isListening
                ? '#fff'
                : (isDark ? '#95a5a6' : 'grey.600'),
              transition: 'all 0.2s ease',
              boxShadow: isListening ? '0 0 0 2px rgba(244, 67, 54, 0.3)' : 'none',
              animation: isListening ? 'pulse 1.5s infinite' : 'none',
              '@keyframes pulse': {
                '0%': {
                  transform: 'scale(1)',
                  boxShadow: '0 0 0 2px rgba(244, 67, 54, 0.3)',
                },
                '50%': {
                  transform: 'scale(1.1)',
                  boxShadow: '0 0 0 4px rgba(244, 67, 54, 0.2)',
                },
                '100%': {
                  transform: 'scale(1)',
                  boxShadow: '0 0 0 2px rgba(244, 67, 54, 0.3)',
                },
              },
              '&:hover': !guardiaListenAccess?.hasAccess ? {} : {
                backgroundColor: isListening
                  ? (isDark ? '#d84315' : '#d32f2f')
                  : (isDark ? '#5a6c7d' : 'grey.200'),
                transform: isListening ? 'scale(1.05)' : 'scale(1.05)',
              },
              '&.Mui-disabled': {
                backgroundColor: isDark ? '#555' : '#ccc',
                color: isDark ? '#7f8c8d' : 'grey.400'
              }
            }}
          >
            {isListening ? <MicOffIcon /> : (isTranscribing ? <CircularProgress size={18} sx={{ color: isDark ? '#95a5a6' : 'grey.600' }} /> : <MicIcon />)}
          </IconButton>
          {isListening && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 0.75,
              borderRadius: '20px',
              backgroundColor: isDark ? 'rgba(244, 67, 54, 0.08)' : 'rgba(244, 67, 54, 0.04)',
              backdropFilter: 'blur(8px)',
              border: `1px solid ${isDark ? 'rgba(244, 67, 54, 0.15)' : 'rgba(244, 67, 54, 0.1)'}`,
              boxShadow: isDark
                ? '0 2px 8px rgba(244, 67, 54, 0.15)'
                : '0 2px 8px rgba(244, 67, 54, 0.08)',
            }}>
              <Box sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: '#f44336',
                ml: 1,
                animation: 'recordingPulse 1.5s infinite',
                '@keyframes recordingPulse': {
                  '0%': { transform: 'scale(1)', opacity: 1 },
                  '50%': { transform: 'scale(1.3)', opacity: 0.7 },
                  '100%': { transform: 'scale(1)', opacity: 1 },
                },
              }} />
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.75rem',
                  color: isDark ? '#ff8a80' : '#d32f2f',
                  fontWeight: 500,
                  letterSpacing: '0.02em',
                  ml: 1,
                }}
              >
                Écoute en cours...
              </Typography>
            </Box>
          )}
          {isTranscribing && !isListening && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 0.75,
              borderRadius: '20px',
              backgroundColor: isDark ? 'rgba(33, 150, 243, 0.08)' : 'rgba(25, 118, 210, 0.06)',
              border: `1px solid ${isDark ? 'rgba(79, 195, 247, 0.25)' : 'rgba(25, 118, 210, 0.2)'}`,
              boxShadow: isDark
                ? '0 2px 8px rgba(25, 118, 210, 0.2)'
                : '0 2px 8px rgba(33, 150, 243, 0.1)'
            }}>
              <CircularProgress size={14} sx={{ mr: 1 }} />
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.75rem',
                  color: isDark ? '#4fc3f7' : '#1976d2',
                  fontWeight: 500,
                  letterSpacing: '0.02em'
                }}
              >
                Transcription en cours...
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Message d'erreur */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            backgroundColor: 'rgba(244, 67, 54, 0.15)',
            color: '#ff6b6b',
            '& .MuiAlert-icon': {
              color: '#ff6b6b'
            }
          }}
        >
          {error}
        </Alert>
      )}
      {listenError && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            backgroundColor: isDark ? 'rgba(244, 67, 54, 0.15)' : 'rgba(244, 67, 54, 0.1)',
            color: isDark ? '#ff8a80' : 'error.main',
            '& .MuiAlert-icon': {
              color: isDark ? '#ff8a80' : 'error.main'
            }
          }}
        >
          {listenError}
        </Alert>
      )}

      {/* Encadré de correction GuardIA */}
      {showCorrectionBox && correctionData.originalText && (
        <Fade in={true} timeout={300}>
          <Paper
            elevation={1}
            sx={{
              mt: 1,
              mb: 2,
              p: 2,
              backgroundColor: isDark ? 'rgba(128, 0, 128, 0.1)' : 'rgba(221, 160, 221, 0.3)',
              borderRadius: 2,
              border: `1px solid ${isDark ? '#c084fc' : '#9370DB'}`,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Croix de fermeture */}
            <IconButton
              onClick={handleCloseCorrectionBox}
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                color: isDark ? '#ecf0f1' : 'text.secondary',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                },
                width: '24px',
                height: '24px'
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>

            {/* Titre */}
            <Typography
              variant="caption"
              sx={{
                fontWeight: 'bold',
                mb: 1,
                color: isDark ? '#c084fc' : '#800080',
                fontSize: '0.75rem',
                pr: 3 // Pour laisser de la place à la croix
              }}
            >
              Votre rubrique corrigée par GuardIA :
            </Typography>

            {/* Contenu de correction */}
            {correctionLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
                <CircularProgress size={20} sx={{ color: isDark ? '#c084fc' : '#800080' }} />
                <Typography sx={{ ml: 1, color: isDark ? '#ecf0f1' : 'text.secondary', fontSize: '0.875rem' }}>
                  Correction en cours...
                </Typography>
              </Box>
            ) : (
              <Box>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.8)',
                    borderRadius: 1,
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                    maxHeight: '200px',
                    overflow: 'auto'
                  }}
                >
                  <CorrectedTextDisplay
                    originalText={correctionData.originalText}
                    correctedText={correctionData.correctedText}
                    differences={correctionData.differences}
                    isDark={isDark}
                  />
                </Paper>
              </Box>
            )}

            {/* Message d'avertissement */}
            <Typography
              variant="caption"
              sx={{
                mt: 1,
                display: 'block',
                textAlign: 'center',
                color: isDark ? '#95a5a6' : 'text.secondary',
                fontSize: '0.7rem',
                fontStyle: 'italic'
              }}
            >
              GuardIA peut commettre des erreurs, vérifiez toujours la correction.
            </Typography>
          </Paper>
        </Fade>
      )}
    </Box>
  );
};

export default GuardiaCheck;
